import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { createPortal } from "react-dom";
import { GuidedTourProps, TourStep } from "./types";
import "./GuidedTour.css";


function getTargetRect(selector: string): DOMRect | null {
    const el = document.querySelector(selector);
    return el ? el.getBoundingClientRect() : null;
}

function computeTooltipPosition(
    targetRect: DOMRect,
    placement: TourStep["placement"],
    tooltipWidth: number,
    tooltipHeight: number,
    padding: number,
    gap = 12,
) {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    tooltipWidth = Math.min(tooltipWidth, vw - 32);

    let resolvedPlacement = placement || "auto";

    if (resolvedPlacement === "auto") {
        const spaceBelow = vh - targetRect.bottom;
        const spaceAbove = targetRect.top;
        const spaceRight = vw - targetRect.right;
        const spaceLeft = targetRect.left;

        if (spaceBelow >= tooltipHeight + gap + padding) {
            resolvedPlacement = "bottom";
        } else if (spaceAbove >= tooltipHeight + gap + padding) {
            resolvedPlacement = "top";
        } else if (spaceRight >= tooltipWidth + gap + padding) {
            resolvedPlacement = "right";
        } else if (spaceLeft >= tooltipWidth + gap + padding) {
            resolvedPlacement = "left";
        } else {
            resolvedPlacement = "bottom";
        }
    }

    let top = 0;
    let left = 0;

    switch (resolvedPlacement) {
        case "bottom":
            top = targetRect.bottom + padding + gap + scrollY;
            left =
                targetRect.left +
                targetRect.width / 2 -
                tooltipWidth / 2 +
                scrollX;
            break;
        case "top":
            top = targetRect.top - padding - gap - tooltipHeight + scrollY;
            left =
                targetRect.left +
                targetRect.width / 2 -
                tooltipWidth / 2 +
                scrollX;
            break;
        case "right":
            top =
                targetRect.top +
                targetRect.height / 2 -
                tooltipHeight / 2 +
                scrollY;
            left = targetRect.right + padding + gap + scrollX;
            break;
        case "left":
            top =
                targetRect.top +
                targetRect.height / 2 -
                tooltipHeight / 2 +
                scrollY;
            left = targetRect.left - padding - gap - tooltipWidth + scrollX;
            break;
    }

    left = Math.max(16, Math.min(left, vw - tooltipWidth - 16));
    top = Math.max(16 + scrollY, Math.min(top, vh - tooltipHeight - 16 + scrollY));

    return {
        top,
        left,
        resolvedPlacement: resolvedPlacement as "top" | "bottom" | "left" | "right",
    };
}


const GuidedTour: React.FC<GuidedTourProps> = ({
    steps,
    tourId,
    autoStart = true,
    onComplete,
    skipText = "Skip",
    nextText = "Next",
    backText = "Back",
    finishText = "Got it!",
    highlightPadding = 8,
    forceShow = false,
    renderTrigger,
}) => {
    const storageKey = `guided_tour_completed_${tourId}`;
    const hasCompleted = !forceShow && localStorage.getItem(storageKey) === "true";

    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [phase, setPhase] = useState<"entering" | "active" | "exiting">(
        "entering",
    );

    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipSize, setTooltipSize] = useState({ width: 340, height: 200 });

    const step = steps[currentStep];


    const startTour = useCallback(() => {
        if (steps.length === 0) return;
        setCurrentStep(0);
        setPhase("entering");
        setIsActive(true);
        setTimeout(() => setPhase("active"), 50);
    }, [steps.length]);

    const completeTour = useCallback(() => {
        step?.onExit?.();
        setPhase("exiting");
        setTimeout(() => {
            setIsActive(false);
            localStorage.setItem(storageKey, "true");
            onComplete?.();
        }, 300);
    }, [onComplete, step, storageKey]);

    const skipTour = useCallback(() => {
        completeTour();
    }, [completeTour]);

    useEffect(() => {
        if (autoStart && !hasCompleted) {
            const id = setTimeout(startTour, 600);
            return () => clearTimeout(id);
        }
    }, [autoStart, hasCompleted, startTour]);


    const goNext = useCallback(() => {
        step?.onExit?.();
        if (currentStep < steps.length - 1) {
            setCurrentStep((s) => s + 1);
        } else {
            completeTour();
        }
    }, [currentStep, steps.length, step, completeTour]);

    const goBack = useCallback(() => {
        step?.onExit?.();
        if (currentStep > 0) {
            setCurrentStep((s) => s - 1);
        }
    }, [currentStep, step]);

    useEffect(() => {
        if (isActive && step) {
            step.onEnter?.();
        }
    }, [currentStep, isActive]);


    useEffect(() => {
        if (!isActive || !step) return;

        const update = () => {
            const rect = getTargetRect(step.targetSelector);
            setTargetRect(rect);

            // Scroll target into view if needed
            if (rect) {
                const el = document.querySelector(step.targetSelector);
                if (el) {
                    const elRect = el.getBoundingClientRect();
                    const inView =
                        elRect.top >= 0 &&
                        elRect.left >= 0 &&
                        elRect.bottom <= window.innerHeight &&
                        elRect.right <= window.innerWidth;
                    if (!inView) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }
            }
        };

        update();
        const intervalId = setInterval(update, 200);
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [isActive, step, currentStep]);


    useEffect(() => {
        if (tooltipRef.current) {
            const { offsetWidth, offsetHeight } = tooltipRef.current;
            setTooltipSize({ width: offsetWidth, height: offsetHeight });
        }
    }, [currentStep, isActive, targetRect]);


    useEffect(() => {
        if (!isActive) return;

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") skipTour();
            if (e.key === "ArrowRight" || e.key === "Enter") goNext();
            if (e.key === "ArrowLeft") goBack();
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [isActive, goNext, goBack, skipTour]);


    useEffect(() => {
        if (!isActive || !step?.advanceOnClick) return;

        const handler = () => {
            setTimeout(goNext, 300);
        };

        const el = document.querySelector(step.advanceOnClick);
        if (el) {
            el.addEventListener("click", handler);
            return () => el.removeEventListener("click", handler);
        }
    }, [isActive, step, goNext]);


    const tooltipPos = useMemo(() => {
    if (!targetRect) return null;
    return computeTooltipPosition(
        targetRect,
        step?.placement,
        tooltipSize.width,
        Math.min(tooltipSize.height, window.innerHeight - 48),
        highlightPadding,
    );
}, [targetRect, step?.placement, tooltipSize, highlightPadding]);


    if (!isActive || !step) {
        const handleLaunch = () => {
            setCurrentStep(0);
            setPhase("entering");
            setIsActive(true);
            setTimeout(() => setPhase("active"), 50);
        };

        if (renderTrigger) {
            return <>{renderTrigger(handleLaunch)}</>;
        }

        if (hasCompleted || !autoStart) {
            return (
                <button
                    className="guided-tour-launch-btn"
                    onClick={handleLaunch}
                    title="Take a tour"
                >
                    <span className="guided-tour-launch-btn__icon">💡</span>
                    Tutorial
                </button>
            );
        }
        return null;
    }

    const spotlightSvg = targetRect ? (
        <svg>
            <defs>
                <mask id="guided-tour-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="#FBE5C6" />
                    <rect
                        x={targetRect.left - highlightPadding}
                        y={targetRect.top - highlightPadding}
                        width={targetRect.width + highlightPadding * 2}
                        height={targetRect.height + highlightPadding * 2}
                        rx="8"
                        ry="8"
                        fill="black"
                    />
                </mask>
            </defs>
            <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.55)"
                mask="url(#guided-tour-mask)"
            />
            \
            <rect
                x={targetRect.left - highlightPadding - 2}
                y={targetRect.top - highlightPadding - 2}
                width={targetRect.width + (highlightPadding + 2) * 2}
                height={targetRect.height + (highlightPadding + 2) * 2}
                rx="10"
                ry="10"
                fill="none"
                stroke="rgba(58,123,213,0.4)"
                strokeWidth="2"
            />
        </svg>
    ) : (
        <svg>
            <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" />
        </svg>
    );

    return createPortal(
        <>
            <div
                className={`guided-tour-overlay guided-tour-overlay--${phase}`}
                onClick={step.advanceOnClick ? undefined : skipTour}
                style={step.advanceOnClick ? { pointerEvents: "none" } : undefined}
            >
                {spotlightSvg}
            </div>

            {tooltipPos && (
                <div
                    ref={tooltipRef}
                    className="guided-tour-tooltip"
                    style={{
                        top: tooltipPos.top,
                        left: tooltipPos.left,
                        pointerEvents: "auto",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="guided-tour-tooltip__accent" />

                    <div
                        className={`guided-tour-tooltip__arrow guided-tour-tooltip__arrow--${tooltipPos.resolvedPlacement === "bottom"
                            ? "bottom"
                            : tooltipPos.resolvedPlacement === "top"
                                ? "top"
                                : tooltipPos.resolvedPlacement === "left"
                                    ? "left"
                                    : "right"
                            }`}
                    />

                    <div className="guided-tour-tooltip__content">
                        <h3 className="guided-tour-tooltip__title">{step.title}</h3>
                        <p className="guided-tour-tooltip__description">
                            {step.description}
                        </p>
                    </div>

                    <div className="guided-tour-tooltip__footer">
                        <div className="guided-tour-tooltip__steps">
                            {steps.map((_, i) => (
                                <div
                                    key={i}
                                    className={`guided-tour-tooltip__step-dot ${i === currentStep
                                        ? "guided-tour-tooltip__step-dot--active"
                                        : i < currentStep
                                            ? "guided-tour-tooltip__step-dot--completed"
                                            : ""
                                        }`}
                                />
                            ))}
                            <span className="guided-tour-tooltip__step-text">
                                Step {currentStep + 1} of {steps.length}
                            </span>
                        </div>

                        <div className="guided-tour-tooltip__actions">
                            {currentStep === 0 ? (
                                <button
                                    className="guided-tour-btn guided-tour-btn--secondary"
                                    onClick={skipTour}
                                >
                                    {skipText}
                                </button>
                            ) : (
                                <button
                                    className="guided-tour-btn guided-tour-btn--secondary"
                                    onClick={goBack}
                                >
                                    {backText}
                                </button>
                            )}
                            {!step.advanceOnClick && (
                                <button
                                    className="guided-tour-btn guided-tour-btn--primary"
                                    onClick={goNext}
                                >
                                    {currentStep === steps.length - 1 ? finishText : nextText}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>,
        document.body,
    );
};

export default GuidedTour;
